import React, { createContext, useContext, useState, useCallback } from 'react';
import type { QuizQuestion, QuizAction, ScoringConfig } from '@/types/quiz';

export interface QuizEditorFormState {
  name: string;
  description: string;
  instructions: string;
  template_id: string;
  stepping_approach: 'single-step' | 'multiple-steps';
  feedback_mode: 'immediate' | 'time-delay' | 'on-submit' | 'after-completion';
  explanation_timing: 'immediate' | 'on-submit' | 'results-only' | 'never';
  questions: QuizQuestion[];
  actions: QuizAction[];
  estimated_seconds_per_question: number;
  scoring_config: ScoringConfig;
}

const initialFormState: QuizEditorFormState = {
  name: '',
  description: '',
  instructions: '',
  template_id: '',
  stepping_approach: 'multiple-steps',
  feedback_mode: 'immediate',
  explanation_timing: 'immediate',
  questions: [],
  actions: [],
  estimated_seconds_per_question: 120,
  scoring_config: {
    mode: 'percentage',
    score_ranges: [],
  },
};


interface QuizEditorContextType {
  isOpen: boolean;
  quizId: string | null;
  isNew: boolean;
  formState: QuizEditorFormState;
  activeTab: string;
  setFormState: React.Dispatch<React.SetStateAction<QuizEditorFormState>>;
  setActiveTab: (tab: string) => void;
  openEditor: (quizId?: string) => void;
  closeEditor: () => void;
  resetEditor: () => void;
  updateFormField: <K extends keyof QuizEditorFormState>(
    field: K,
    value: QuizEditorFormState[K]
  ) => void;
}

const QuizEditorContext = createContext<QuizEditorContextType | undefined>(undefined);

export function QuizEditorProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [quizId, setQuizId] = useState<string | null>(null);
  const [formState, setFormState] = useState<QuizEditorFormState>(initialFormState);
  const [activeTab, setActiveTab] = useState('details');

  const isNew = !quizId;

  const openEditor = useCallback((id?: string) => {
    if (id) {
      setQuizId(id);
    } else {
      if (!isOpen) {
        setQuizId(null);
        setFormState(initialFormState);
      } else {
        setQuizId(null);
      }
    }
    setIsOpen(true);
  }, [isOpen]);

  const closeEditor = useCallback(() => {
    setIsOpen(false);
  }, []);

  const resetEditor = useCallback(() => {
    setFormState(initialFormState);
    setQuizId(null);
    setActiveTab('details');
  }, []);

  const updateFormField = useCallback(<K extends keyof QuizEditorFormState>(
    field: K,
    value: QuizEditorFormState[K]
  ) => {
    setFormState(prev => ({ ...prev, [field]: value }));
  }, []);

  return (
    <QuizEditorContext.Provider
      value={{
        isOpen,
        quizId,
        isNew,
        formState,
        activeTab,
        setFormState,
        setActiveTab,
        openEditor,
        closeEditor,
        resetEditor,
        updateFormField,
      }}
    >
      {children}
    </QuizEditorContext.Provider>
  );
}

export function useQuizEditor() {
  const context = useContext(QuizEditorContext);
  if (!context) {
    throw new Error('useQuizEditor must be used within QuizEditorProvider');
  }
  return context;
}
